import { siteInstant, shiftDay, siteDay, formatSiteDate } from "@/lib/site-time";
/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAnalyticsReport } from "@/lib/analytics-admin.functions";
import type { FunnelResult } from "@/lib/analytics-calc";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/statistieken")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Statistieken | Beheer Gopro Projects" },
      { name: "description", content: "Bezoekers, herkomst en offerte-aanvragen van de website." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Statistieken | Beheer Gopro Projects" },
      { property: "og:description", content: "Bezoekers, herkomst en offerte-aanvragen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: StatsPage,
});

const PERIODS = [
  { key: "7d", label: "7 dagen", days: 7 },
  { key: "30d", label: "30 dagen", days: 30 },
  { key: "90d", label: "90 dagen", days: 90 },
  { key: "12m", label: "12 maanden", days: 365 },
] as const;

const LABELS: Record<string, string> = {
  google: "Google", social: "Sociale media", direct: "Rechtstreeks", referral: "Andere sites", other_search: "Andere zoekmachines",
  mobile: "Gsm", tablet: "Tablet", desktop: "Computer",
  click_phone: "Telefoon", click_email: "E-mail", click_whatsapp: "WhatsApp",
  header: "Menu", footer: "Voettekst", page: "Pagina",
  nieuwbouw: "Nieuwbouw", renovatie: "Renovatie", techniek: "Techniek", warmtepomp: "Warmtepomp", overige: "Overige",
};
const l = (k: string) => LABELS[k] ?? k;
const nf = new Intl.NumberFormat("nl-BE");
const pf = new Intl.NumberFormat("nl-BE", { style: "percent", maximumFractionDigits: 1 });

function Delta({ now, before }: { now: number; before: number }) {
  if (!before) return <span className="text-sm text-muted-foreground">vorige periode: 0</span>;
  const d = (now - before) / before;
  return <span className="text-sm text-muted-foreground">{d >= 0 ? "+" : ""}{pf.format(d)} t.o.v. vorige periode</span>;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="mb-4 font-display text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function List({ items, empty = "Nog geen gegevens." }: { items: { key: string; count: number }[]; empty?: string }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  const max = Math.max(...items.map((i) => i.count));
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.key} className="text-sm">
          <div className="flex justify-between gap-4"><span className="truncate text-foreground">{l(i.key)}</span><span className="tabular-nums text-foreground">{nf.format(i.count)}</span></div>
          <div className="mt-1 h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-accent" style={{ width: `${(i.count / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

function Funnel({ steps }: { steps: FunnelResult }) {
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => (
        <li key={s.key} className="flex justify-between gap-4 text-sm">
          <span className="text-foreground">{i + 1}. {s.label}</span>
          <span className="tabular-nums text-foreground">{nf.format(s.sessions)} <span className="text-muted-foreground">({pf.format(s.rate)})</span></span>
        </li>
      ))}
    </ol>
  );
}

function SegmentFunnels({ data }: { data: Record<string, FunnelResult> }) {
  const entries = Object.entries(data);
  if (!entries.length) return <p className="text-sm text-muted-foreground">Nog geen gegevens.</p>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-medium">Segment</th><th className="font-medium">Bezoeken</th><th className="font-medium">Offertepagina</th><th className="font-medium">Aanvragen</th></tr></thead>
      <tbody>
        {entries.map(([k, f]) => (
          <tr key={k} className="border-t border-border"><td className="py-1">{l(k)}</td><td className="tabular-nums">{f[0]?.sessions ?? 0}</td><td className="tabular-nums">{f[1]?.sessions ?? 0}</td><td className="tabular-nums">{f[3]?.sessions ?? 0} ({pf.format(f[3]?.rate ?? 0)})</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function StatsPage() {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]["key"] | "custom">("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showBots, setShowBots] = useState(false);
  const [source, setSource] = useState("");
  const [device, setDevice] = useState("");
  const [lang, setLang] = useState("");
  const fetchReport = useServerFn(getAnalyticsReport);

  const range = useMemo(() => {
    if (period === "custom" && customFrom && customTo) {
      return { from: siteInstant(customFrom).toISOString(), to: siteInstant(shiftDay(customTo, 1)).toISOString() };
    }
    const days = PERIODS.find((p) => p.key === period)?.days ?? 30;
    const to = new Date();
    return { from: siteInstant(shiftDay(siteDay(to), -(days - 1))).toISOString(), to: to.toISOString() };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute only when the period changes
  }, [period, customFrom, customTo]);

  const query = useQuery({
    queryKey: ["analytics", range, showBots, source, device, lang],
    queryFn: () => fetchReport({ data: { ...range, showBots, source: source || undefined, device: device || undefined, lang: lang || undefined } }),
  });
  const r = query.data;
  const select = "h-10 rounded border border-input bg-background px-3 text-sm";

  return (
    <div className="mx-auto max-w-[1280px] px-5 py-10 md:px-16">
      <AdminNav />
      <h1 className="font-display text-3xl font-semibold text-foreground">Statistieken</h1>
      <p className="mt-2 max-w-[70ch] text-muted-foreground">Eigen meting zonder persoonsgegevens. Beheerders en de preview worden niet meegeteld.</p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        {PERIODS.map((p) => (
          <Button key={p.key} variant={period === p.key ? "default" : "outline"} onClick={() => setPeriod(p.key)}>{p.label}</Button>
        ))}
        <Button variant={period === "custom" ? "default" : "outline"} onClick={() => setPeriod("custom")}>Eigen periode</Button>
        {period === "custom" && (
          <>
            <label className="text-sm">Van<input type="date" className={`${select} ml-2`} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} /></label>
            <label className="text-sm">Tot<input type="date" className={`${select} ml-2`} value={customTo} onChange={(e) => setCustomTo(e.target.value)} /></label>
          </>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="text-sm">Herkomst <select className={select} value={source} onChange={(e) => setSource(e.target.value)}><option value="">Alle</option>{["google", "social", "direct", "referral", "other_search"].map((s) => <option key={s} value={s}>{l(s)}</option>)}</select></label>
        <label className="text-sm">Toestel <select className={select} value={device} onChange={(e) => setDevice(e.target.value)}><option value="">Alle</option>{["mobile", "tablet", "desktop"].map((s) => <option key={s} value={s}>{l(s)}</option>)}</select></label>
        <label className="text-sm">Taal <select className={select} value={lang} onChange={(e) => setLang(e.target.value)}><option value="">Alle</option>{["nl", "en", "fr"].map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}</select></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showBots} onChange={(e) => setShowBots(e.target.checked)} /> Toon bots</label>
      </div>

      {query.isLoading && <p className="mt-8 text-muted-foreground" role="status">Statistieken worden geladen.</p>}
      {query.isError && <p className="mt-8 text-destructive" role="alert">De statistieken konden niet geladen worden. Probeer het later opnieuw.</p>}
      {r && r.sessions === 0 && <p className="mt-8 text-muted-foreground">Nog geen bezoeken in deze periode. De meting start na het publiceren van de site.</p>}

      {r && (
        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <Card title="Bezoeken"><p className="font-display text-3xl font-semibold tabular-nums">{nf.format(r.sessions)}</p><Delta now={r.sessions} before={r.previous.sessions} /><p className="mt-2 text-sm text-muted-foreground">{nf.format(r.visitors)} unieke bezoekers (met toestemming), {nf.format(r.bots)} botverzoeken verborgen</p></Card>
          <Card title="Bekeken pagina's"><p className="font-display text-3xl font-semibold tabular-nums">{nf.format(r.pageViews)}</p><Delta now={r.pageViews} before={r.previous.pageViews} /></Card>
          <Card title="Offerte-aanvragen"><p className="font-display text-3xl font-semibold tabular-nums">{nf.format(r.leads)}</p><Delta now={r.leads} before={r.previous.leads} /><p className="mt-2 text-sm text-muted-foreground">{nf.format(r.formAbandons)} keer formulier verlaten zonder verzenden</p></Card>

          <Card title="Bezoeken per dag">
            {r.daily.length ? (
              <ul className="space-y-1 text-sm">{r.daily.slice(-31).map((d) => <li key={d.day} className="flex justify-between"><span>{formatSiteDate(d.day, "nl-BE", { day: "numeric", month: "short" })}</span><span className="tabular-nums">{d.sessions} bezoeken, {d.pageViews} pagina's</span></li>)}</ul>
            ) : <p className="text-sm text-muted-foreground">Nog geen gegevens.</p>}
          </Card>
          <Card title="Offerte-trechter"><Funnel steps={r.funnel} /><p className="mt-3 text-sm text-muted-foreground">Mediane tijd tot aanvraag: {r.medianSecondsToLead === null ? "nog geen aanvragen" : `${Math.round(r.medianSecondsToLead / 60)} min`}</p></Card>
          <Card title="Herkomst"><List items={r.sources} /></Card>
          <Card title="Toestel"><List items={r.devices} /></Card>
          <Card title="Taal"><List items={r.languages.map((i) => ({ ...i, key: i.key.toUpperCase() }))} /></Card>
          <Card title="Taalwissels"><List items={r.languageSwitches} /></Card>
          <Card title="Telefoon, e-mail en WhatsApp"><List items={r.contactClicks} /><p className="mt-3 text-sm text-muted-foreground">{r.phoneThenLead} bezoeken belden en vroegen daarna nog een offerte aan.</p></Card>
          <Card title="Contactklikken per pagina"><List items={r.contactByPage} /></Card>
          <Card title="Klikken op Offerte aanvragen"><List items={r.quoteButtons} /></Card>
          <Card title="Aanvragen per dienst"><List items={r.leadsByService} /></Card>
          <Card title="Aanvragen per instappagina"><List items={r.leadsByEntryPage} /></Card>
          <Card title="Meest bekeken pagina's"><List items={r.topPages} /></Card>
          <Card title="Scrolldiepte artikels en realisaties"><List items={r.scrollDepth} /></Card>
          <div className="lg:col-span-3 grid gap-6 lg:grid-cols-3">
            <Card title="Trechter per herkomst"><SegmentFunnels data={r.funnelBySource} /></Card>
            <Card title="Trechter per toestel"><SegmentFunnels data={r.funnelByDevice} /></Card>
            <Card title="Trechter per taal"><SegmentFunnels data={r.funnelByLang} /></Card>
          </div>
        </div>
      )}
    </div>
  );
}
